import { z } from "zod";
export const apiVersion = "v1";
export const providerSchema = z.enum(["youtube", "tiktok", "instagram"]);
export const signingRequestStatusSchema = z.enum([
    "resolving_source",
    "awaiting_source_selection",
    "queued",
    "signing",
    "completed",
    "failed"
]);
/** Fixed milestones, produced only by the signing control plane. */
export const signingProgressPhaseSchema = z.enum([
    "queued",
    "acquiring_source",
    "signing_video",
    "storing_evidence",
    "finalizing_record",
    "completed",
    "failed"
]);
export const signingProgressSchema = z
    .object({
    phase: signingProgressPhaseSchema,
    percent: z.number().int().min(0).max(100)
})
    .strict();
/**
 * Fine-grained, non-authoritative work measurements streamed to a Studio
 * session. They never participate in a signing or verification verdict.
 *
 * Values are deliberately expressed in an explicit unit instead of inventing
 * a percentage for work whose total is not known by a public origin (for
 * example a chunked platform download). This lets the UI say what is actually
 * happening without treating a guessed percentage as product data.
 */
export const signingProgressStageSchema = z.enum([
    "queued",
    "source_discovery",
    "source_download",
    "source_assembly",
    "source_inspection",
    "evidence_preparation",
    "source_staging",
    "reducer_inputs",
    "luma_evidence",
    "v3_atlas_anchors",
    "audio_avlink",
    "sealing_segments",
    "uploading_evidence",
    "finalizing_record",
    "completed",
    "failed"
]);
/** One chronological order for lifecycle presentation, including fan-out I/O. */
export const signingExecutionStages = [
    "source_discovery", "source_download", "source_inspection", "source_assembly",
    "evidence_preparation", "source_staging", "luma_evidence", "reducer_inputs",
    "v3_atlas_anchors", "audio_avlink", "sealing_segments", "uploading_evidence", "finalizing_record"
];
export function signingProgressStageRank(stage) {
    if (stage === "completed" || stage === "failed")
        return signingExecutionStages.length + 1;
    return stage === "queued" ? 0 : signingExecutionStages.indexOf(stage) + 1;
}
export function mostAdvancedSigningProgressEvent(events) {
    return Object.values(events).reduce((previous, candidate) => !previous || signingProgressStageRank(candidate.stage) >= signingProgressStageRank(previous.stage) ? candidate : previous, undefined);
}
/** Operational measurements only, never evidence or a verification decision. */
export const preparationTimingSchema = z.object({
    timelineMs: z.number().int().min(0).max(86_400_000),
    digestMs: z.number().int().min(0).max(86_400_000),
    audioMs: z.number().int().min(0).max(86_400_000),
    sealMs: z.number().int().min(0).max(86_400_000),
    totalMs: z.number().int().min(0).max(86_400_000),
    nativeThreads: z.number().int().min(1).max(1024),
    countBasis: z.enum(["indexed_expectation", "decoded_fallback"])
}).strict();
export const signingProgressStateSchema = z.enum(["running", "completed"]);
export const signingProgressUnitSchema = z.enum(["bytes", "frames", "anchors", "segments", "artifacts"]);
const progressMeasurementSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const signingProgressEventBaseSchema = z
    .object({
    stage: signingProgressStageSchema,
    state: signingProgressStateSchema,
    unit: signingProgressUnitSchema.optional(),
    current: progressMeasurementSchema.optional(),
    total: progressMeasurementSchema.optional(),
    /**
     * D1 stores only this coarse durable checkpoint. A detailed event without
     * a checkpoint is broadcast through the progress broker only.
     */
    checkpoint: signingProgressPhaseSchema.optional(),
    preparationTiming: preparationTimingSchema.optional()
})
    .strict();
/** Input accepted only from the private signing runner. */
export const signingProgressEventInputSchema = signingProgressEventBaseSchema
    .superRefine((value, context) => {
    if (value.preparationTiming && (value.stage !== "evidence_preparation" || value.state !== "completed")) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "preparation timing requires completed preparation" });
    }
    if ((value.current === undefined) !== (value.unit === undefined)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "progress unit and current must be supplied together" });
    }
    if (value.total !== undefined && value.current === undefined) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "progress total requires current" });
    }
    if (value.total !== undefined && value.current !== undefined && value.current > value.total) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "progress current cannot exceed total" });
    }
});
/** Event delivered over the authenticated, job-scoped progress socket. */
export const signingProgressEventSchema = signingProgressEventBaseSchema
    .extend({
    sequence: z.number().int().positive(),
    emittedAt: z.string().datetime()
})
    .strict()
    .superRefine((value, context) => {
    // WebSocket payloads are untrusted at the browser boundary as well. Keep
    // the same closed measurement contract used by the private runner route.
    if (value.preparationTiming && (value.stage !== "evidence_preparation" || value.state !== "completed")) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "preparation timing requires completed preparation" });
    }
    if ((value.current === undefined) !== (value.unit === undefined)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "progress unit and current must be supplied together" });
    }
    if (value.total !== undefined && value.current === undefined) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "progress total requires current" });
    }
    if (value.total !== undefined && value.current !== undefined && value.current > value.total) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "progress current cannot exceed total" });
    }
});
export const signingFailureCodeSchema = z.enum([
    "ingest_failed",
    "core_failed",
    /** Closed runner diagnostic: the bounded signing runtime ran out of a resource. */
    "core_resource_exhausted",
    /** Closed runner diagnostic: a required native Core runtime was unavailable. */
    "core_runtime_unavailable",
    /** Closed runner diagnostic: the acquired media cannot be processed by the Core. */
    "core_media_unsupported",
    /** Closed runner diagnostic: the source-free local-crop evidence could not be produced. */
    "core_artifact_generation_failed",
    /** Closed runner diagnostic when no more specific safe category applies. */
    "core_execution_failed",
    /** The distributed long-form coordinator could not persist its private plan. */
    "distributed_preparation_failed",
    /** The bounded source shards could not reach the distributed work layout. */
    "distributed_source_transfer_failed",
    /** The distributed source layout could not activate independent segments. */
    "distributed_source_activation_failed",
    "artifact_upload_failed",
    "unsupported_source",
    "runner_failed",
    "source_selection_required",
    "source_media_unavailable",
    "source_acquisition_failed",
    "source_inspection_failed",
    "source_assembly_failed",
    "source_artifact_too_large",
    "source_cleanup_failed",
    "source_acquisition_timed_out",
    "signing_job_timed_out",
    "runner_lease_expired",
    /** A creator deliberately stopped an active request before a record existed. */
    "cancelled_by_owner"
]);
export const publicUrlSourceSchema = z
    .object({
    kind: z.literal("public_url"),
    url: z.string().url().max(2048),
    preferredProvider: providerSchema.optional()
})
    .strict();
export const uploadSourceSchema = z
    .object({
    kind: z.literal("upload"),
    fileName: z.string().trim().min(1).max(255),
    contentType: z.enum(["video/mp4", "video/quicktime", "video/webm"]),
    byteSize: z.number().int().positive().max(5_000_000_000)
})
    .strict();
export const signingSourceSchema = z.discriminatedUnion("kind", [
    publicUrlSourceSchema,
    uploadSourceSchema
]);
export const createSigningRequestSchema = z
    .object({
    source: signingSourceSchema,
    title: z.string().trim().min(1).max(160).optional(),
    /** A creator declaration, not proof of channel ownership or copyright. */
    sourceRightsConfirmed: z.literal(true)
})
    .strict();
export const sourceCandidateSchema = z
    .object({
    id: z.string().uuid(),
    provider: providerSchema,
    label: z.string().min(1).max(160),
    canonicalUrl: z.string().url().max(2048),
    selected: z.boolean()
})
    .strict();
/**
 * The four fixed objects make up the short-form V3 Local Crop V2 record.
 * Long-form evidence has one signed root and a bounded, numbered sequence of
 * signed segments. Keeping the segment number in the kind makes each object
 * independently addressable without ever accepting a path-like browser value.
 */
export const longFormSegmentArtifactKindPattern = /^longform_segment_[0-9]{6}$/;
export const signingArtifactKindSchema = z.union([
    z.literal("registry"),
    z.literal("local_crop_bundle"),
    z.literal("local_crop_atlas"),
    z.literal("orb_coordinates"),
    z.literal("longform_root"),
    z.string().regex(longFormSegmentArtifactKindPattern)
]);
/** Exact evidence layout retained for a record. */
export const signingEvidenceProfileSchema = z.enum([
    "vc-v3-local-crop-mvp-v2",
    "vc-v3-longform-evidence-v1",
    "vc-v3-longform-luma-evidence-v2"
]);
/**
 * The evidence layout selected by the server when a request is created.
 * It is intentionally returned to the Studio so a queued request never
 * appears to use a different protocol merely because it has not completed.
 */
export const compactLongFormEvidenceProfile = "vc-v3-longform-luma-evidence-v2";
/**
 * Internal long-form work never travels through a browser-facing endpoint.
 * These strict shapes are shared only by the Platform coordinator and a
 * future private segment runner, so a Queue replay cannot invent media,
 * URLs, object keys or a signing policy.
 */
/**
 * V1 remains parseable for existing private staging data. New distributed
 * work is explicitly bound to the integrated V2 shadow profile; neither
 * profile is an R1/R2 public-verdict contract.
 */
export const longFormEvidenceProfileSchema = z.enum([
    "vc-v3-longform-evidence-v1",
    "vc-v3-longform-luma-evidence-v2"
]);
export const longFormExecutionModeSchema = z.enum(["serial", "parallel"]);
export const longFormSegmentTaskSchema = z
    .object({
    profile: longFormEvidenceProfileSchema,
    requestId: z.string().uuid(),
    segmentIndex: z.number().int().nonnegative(),
    startFrame: z.number().int().nonnegative(),
    endFrameExclusive: z.number().int().positive(),
    gopFrames: z.number().int().positive(),
    attempt: z.number().int().nonnegative()
})
    .strict()
    .superRefine((task, context) => {
    if (task.endFrameExclusive <= task.startFrame) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "segment interval must be non-empty" });
    }
    if (task.startFrame % task.gopFrames !== 0) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "segment start must align to a GOP" });
    }
});
export const signingRecordSchema = z
    .object({
    contentId: z.string().regex(/^[a-f0-9]{16,128}$/i),
    /** A shadow record is private instrumentation, never a public claim. */
    verificationState: z.enum(["published", "shadow"]),
    publicVerificationId: z.string().regex(/^[A-Za-z0-9_-]{22,64}$/).optional(),
    publicVerificationUrl: z.string().url().max(2048).optional(),
    registryDigest: z.string().regex(/^[a-f0-9]{64}$/i),
    evidenceProfile: signingEvidenceProfileSchema,
    completedAt: z.string().datetime()
})
    .strict();
export const signingRequestSchema = z
    .object({
    id: z.string().uuid(),
    title: z.string(),
    status: signingRequestStatusSchema,
    source: signingSourceSchema,
    provider: providerSchema.optional(),
    candidates: z.array(sourceCandidateSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    protocol: z.literal("V3 Local Crop V2"),
    /** Server-selected immutable evidence contract for this request. */
    evidenceProfile: signingEvidenceProfileSchema,
    progress: signingProgressSchema,
    failureCode: signingFailureCodeSchema.optional(),
    isMock: z.boolean(),
    record: signingRecordSchema.optional()
})
    .strict();
export const signingRequestPageSchema = z
    .object({
    items: z.array(signingRequestSchema),
    nextCursor: z.string().nullable()
})
    .strict();
/** Result of an owner-scoped cleanup of active, unrecorded signing work. */
export const signingCancellationResultSchema = z
    .object({
    cancelledRequestIds: z.array(z.string().uuid())
})
    .strict();
export const userSchema = z
    .object({
    id: z.string().uuid(),
    name: z.string().min(1).max(120),
    email: z.string().email(),
    role: z.literal("creator")
})
    .strict();
export const studioOverviewSchema = z
    .object({
    user: userSchema,
    metrics: z
        .object({
        totalRequests: z.number().int().nonnegative(),
        queuedRequests: z.number().int().nonnegative(),
        completedRequests: z.number().int().nonnegative()
    })
        .strict(),
    recentRequests: z.array(signingRequestSchema),
    isMock: z.boolean()
})
    .strict();
export const loginInputSchema = z
    .object({
    email: z.string().trim().email().max(254),
    password: z.string().min(12).max(1024)
})
    .strict();
export const oidcProviderSchema = z.enum(["google", "apple"]);
export const beginOidcAuthorizationInputSchema = z
    .object({ provider: oidcProviderSchema })
    .strict();
export const oidcProvidersSchema = z
    .object({ providers: z.array(oidcProviderSchema) })
    .strict();
export const oidcAuthorizationSchema = z
    .object({ authorizationUrl: z.string().url().max(4096) })
    .strict();
export const sessionSchema = z
    .object({
    accessToken: z.string().min(1),
    expiresIn: z.number().int().positive(),
    user: userSchema,
    isMock: z.boolean()
})
    .strict();
export const problemSchema = z
    .object({
    type: z.string().url(),
    title: z.string(),
    status: z.number().int(),
    code: z.string(),
    traceId: z.string().uuid()
})
    .strict();
